import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import Select from 'react-select';
import { MenuItem } from './MenuItem';
import { MenuCategory } from './MenuCategory';
import { Pagination } from './Pagination';
import FoodMenuService from '../../../Services/QRCodeServices/CustomerService/FoodMenuService';
import { OrderMenu } from './OrderMenu';
import { Triangle } from 'react-loader-spinner';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { TabbedOrderPage } from './TabbedOrderPage';

export const FoodMenu = () => {
  const [menuData, setMenuData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [userInfo, setUserInfo] = useState(null);
  const [orgCode, setOrgCode] = useState(null);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);
      setOrgCode(parsedUserInfo?.organizationInfo?.orgCode || null);
    }
  }, []);

  useEffect(() => {
    const fetchMenuData = async () => {
      if (!orgCode) {
        setError('Organization code is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await FoodMenuService.getFoodMenu(orgCode);
        
        if (!data || !data.allMenu) {
          throw new Error('Invalid menu data received');
        }
        
        setMenuData(data.allMenu);
      } catch (error) {
        setError(
          error.response?.status === 500
            ? 'Server error. Please try again later.'
            : 'Failed to load menu. Please check your connection and try again.'
        );
        console.error('Failed to fetch menu data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuData();
  }, [orgCode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const getAllMenuItems = () => {
    return menuData.reduce((items, category) => {
      return [...items, ...category.menuItem];
    }, []);
  };

  const getFilteredContent = () => {
    let filteredItems = [];

    if (selectedCategory === 'all') {
      filteredItems = getAllMenuItems();
    } else {
      const selectedCategoryData = menuData.find(category => category.name === selectedCategory);
      filteredItems = selectedCategoryData ? selectedCategoryData.menuItem : [];
    }

    return filteredItems.filter(item =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getPaginatedContent = () => {
    const filteredContent = getFilteredContent();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContent.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(getFilteredContent().length / itemsPerPage);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  if (isLoading) {
    return (
      <div className="text-center h-100 d-flex w-100 justify-content-center align-items-center p-4">
        <Triangle
                        visible={true}
                        height="80"
                        width="80"
                        color="#5B71B9"
                        ariaLabel="triangle-loading"
                      />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="p-0 mx-2 row">
              {/* <BreadCrumb title="Organization Order Page" pageTitle="Organization POS" className='container-fluid'/> */}
      <Col md={8} className='container'>
      {/* Horizontal scrollable categories */}
            {/* Search bar */}
            <Row className="mb-4 container">
              <Col md={6} xs={12}>
              <h4 className="m-0 p-0">{userInfo.organizationInfo.name}</h4>
              </Col>
        <Col md={6} xs={12}>
          <input
            type="search"
            className="form-control"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>
      <div className="mb-4 position-relative container">
        <div className="d-flex overflow-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div 
            className={`flex-shrink-0 me-3 cursor-pointer ${selectedCategory === 'all' ? 'border-bottom border-primary' : ''}`}
            onClick={() => handleCategoryClick('all')}
            style={{ minWidth: '150px' }}
          >
            <div className="text-center p-3 bg-white rounded-3 shadow-sm">
              <p className="fw-bold mb-0 text-black">All Categories</p>
              <small className="text-muted">{getAllMenuItems().length} items</small>
            </div>
          </div>
          {menuData.map((category, index) => (
            <div 
              key={index}
              className={`flex-shrink-0 me-3  cursor-pointer ${selectedCategory === category.name ? 'border-bottom border-primary' : ''}`}
              onClick={() => handleCategoryClick(category.name)}
              style={{ minWidth: '120px' }}
            >
              <div className="text-center p-3 bg-white rounded-3 shadow-sm">
                <img
                  src={category?.cloudR2ImagePath?.getCDNUrl() || 'https://via.placeholder.com/50'}
                  alt={category.name}
                  className="mb-2 rounded-circle"
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
                <p className="fw-bold mb-0 text-black">{category.name}</p>
                <small className="text-muted">{category.menuItem.length} items</small>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Menu items grid */}
      <Row className='p-0 m-0'>
        {getPaginatedContent().map((item, index) => (
          <MenuItem key={item.id || index} item={item} />
        ))}
      </Row>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
      </Col>
      <Col md={4}>
      <TabbedOrderPage />
      </Col>
    </div>
  );
};

